import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { ClipboardCheck, Clock, User, Briefcase, MapPin, CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import RefreshIndicator from '../../components/RefreshIndicator';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// iter316 — friendly return slot label, e.g. "Wednesday, Nov 4 — PM".
const WINDOW_TXT = { am: 'AM', pm: 'PM', all_day: 'All Day' };
const slotLabel = (s) => {
  if (!s?.date) return '';
  const day = new Date(`${s.date}T12:00:00`).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
  const t = s.window === 'specific' && s.time ? new Date(`${s.date}T${s.time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : WINDOW_TXT[s.window] || '';
  return `${day} — ${t}`;
};


const STATUS_TABS = [
  { key: 'pending_approval',   label: 'Pending'   },
  { key: 'pending_invoice',    label: 'Pending Invoice' },   // approved but no invoice decision yet
  { key: 'approved',           label: 'Approved'  },
  { key: 'revisions_requested',label: 'Revisions' },
];

const INVOICE_OPTIONS = [
  { key: 'create_invoice', label: 'Create Invoice'  },
  { key: 'save_draft',     label: 'Save Draft'      },
  { key: 'send_invoice',   label: 'Send Invoice'    },
  { key: 'paid',           label: 'Paid'            },
  { key: 'warranty',       label: 'Warranty'        },
  { key: 'hsp',            label: 'HSP'             },
  { key: 'return_visit',   label: 'Return Visit'    },
];

const REVISION_PROMPTS = [
  { key: 'serial_number_needed', label: 'Need serial #' },
  { key: 'photo_needed',         label: 'Need photo' },
  { key: 'better_notes',         label: 'Need better notes' },
  { key: 'materials_missing',    label: 'Materials list missing' },
  { key: 'hours_unclear',        label: 'Hours unclear' },
];

const STATUS_BADGES = {
  pending_approval:    'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40',
  approved:            'bg-green-900/30 text-green-400 border-green-700/40',
  revisions_requested: 'bg-red-900/30 text-red-400 border-red-700/40',
};

const JobApprovals = () => {
  const [status, setStatus] = useState('pending_approval');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending_approval: 0, approved: 0, revisions_requested: 0, pending_invoice: 0 });
  const [openItem, setOpenItem] = useState(null);
  const [revisionNote, setRevisionNote] = useState('');
  const [revisionItems, setRevisionItems] = useState([]);
  const [invoiceStatus, setInvoiceStatus] = useState('create_invoice');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      let url = `${API}/admin/job-completions?status=${status}`;
      if (status === 'pending_invoice') url = `${API}/admin/job-completions/pending-invoice`;
      const { data } = await axios.get(url, { withCredentials: true });
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    }
  }, [status]);

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/job-completions/counts`, { withCredentials: true });
      setCounts(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); loadCounts(); }, [load, loadCounts]);

  // iter83 — Auto-refresh job approval queue (20s), paused while review drawer is open.
  const refreshAll = useCallback(async () => {
    await Promise.all([load(), loadCounts()]);
  }, [load, loadCounts]);
  const auto = useAutoRefresh(refreshAll, { pausedByWizard: !!openItem });

  const approve = async (it) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/job-completions/${it.id}/approve`, {}, { withCredentials: true });
      toast.success(it.visit_outcome === 'return_visit' ? 'Visit approved — Job stays open for the return visit' : 'Job approved — appointment marked completed');
      setOpenItem(null);
      load(); loadCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
    setBusy(false);
  };

  const reject = async (it) => {
    if (!revisionNote.trim()) {
      toast.error('Please tell the tech what to revise.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/job-completions/${it.id}/reject`,
        { revision_note: revisionNote.trim(), revision_items: revisionItems },
        { withCredentials: true });
      toast.success('Revisions requested — tech has been notified');
      setRevisionNote(''); setRevisionItems([]);
      setOpenItem(null);
      load(); loadCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    }
    setBusy(false);
  };

  const submitInvoiceDecision = async (it) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/job-completions/${it.id}/invoice-decision`,
        { invoice_status: invoiceStatus, notes: '' },
        { withCredentials: true });
      toast.success(`Invoice status set: ${INVOICE_OPTIONS.find(o => o.key === invoiceStatus)?.label}`);
      setOpenItem(null);
      load(); loadCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  const toggleRevisionItem = (k) => {
    setRevisionItems((arr) => arr.includes(k) ? arr.filter(x => x !== k) : [...arr, k]);
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div data-testid="job-approvals-page" className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Job Approval Queue</h2>
          <p className="text-[#A3A3A3]">Review and approve tech-submitted job completions before they hit the customer record.</p>
        </div>
        <RefreshIndicator
          refresh={auto.refresh}
          lastUpdated={auto.lastUpdated}
          isFetching={auto.isFetching}
          isPaused={auto.isPaused}
          testId="job-approvals-refresh"
        />
      </div>

      {/* Status tabs */}
      <div data-testid="job-approvals-tabs" className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`job-approvals-tab-${t.key}`}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              status === t.key
                ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
                : 'bg-[#0A0A0A] border-[#262626] text-[#A3A3A3] hover:border-[#3f3f3f] hover:text-white'
            }`}
          >
            {t.label} <span className="opacity-70">({counts[t.key] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="grid gap-3">
        {items.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">
            No items in <span className="text-white font-medium">{STATUS_TABS.find(t => t.key === status)?.label}</span>.
          </div>
        ) : items.map((it) => (
          <button
            key={it.id}
            data-testid={`job-approval-row-${it.id}`}
            onClick={() => setOpenItem(it)}
            className="bg-[#141414] border border-[#262626] rounded-md p-4 text-left hover:border-[#D4AF37] transition-colors w-full"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-white font-medium truncate">
                    {it.shift_title || it.service_type || 'Job'} {it.customer_name && <span className="text-[#A3A3A3] font-normal">— {it.customer_name}</span>}
                  </p>
                  <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded border font-bold ${STATUS_BADGES[it.status] || ''}`}>
                    {it.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#A3A3A3] text-xs">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" /> {it.employee_name || it.submitted_by_name}</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {it.service_type || '—'}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(it.submitted_at)}</span>
                  {it.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {it.address}</span>}
                </div>
                <p className="text-[#737373] text-sm mt-2 line-clamp-2">{it.work_performed}</p>
                {it.visit_outcome === 'return_visit' && <span data-testid={`approval-return-badge-${it.id}`} className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider text-sky-300 border border-sky-500/40 rounded px-1.5 py-0.5">Return Visit Needed</span>}
              </div>
              <ClipboardCheck className="w-5 h-5 text-[#D4AF37] shrink-0" />
            </div>
          </button>
        ))}
      </div>

      {/* Detail sheet */}
      <Sheet open={!!openItem} onOpenChange={(o) => { if (!o) { setOpenItem(null); setRevisionNote(''); } }}>
        <SheetContent className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-lg overflow-y-auto" data-testid="job-approval-detail">
          {openItem && (
            <>
              <SheetHeader>
                <SheetTitle className="text-white">
                  {openItem.shift_title || openItem.service_type || 'Job Completion'}
                </SheetTitle>
              </SheetHeader>
              <div className="space-y-5 mt-6">
                <div className="space-y-1">
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Status</p>
                  <span className={`inline-block text-[10px] tracking-wider uppercase px-2 py-0.5 rounded border font-bold ${STATUS_BADGES[openItem.status] || ''}`}>
                    {openItem.status.replace('_', ' ')}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Customer</p>
                    <p className="text-white">{openItem.customer_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Tech</p>
                    <p className="text-white">{openItem.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Date</p>
                    <p className="text-white">{openItem.date}</p>
                  </div>
                  <div>
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Hours</p>
                    <p className="text-white">{openItem.hours_worked ?? '—'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Work Performed</p>
                  <p className="text-white whitespace-pre-wrap">{openItem.work_performed}</p>
                </div>

                {/* iter316 — non-final Visit: show why/when the tech must return. Approving never invoices. */}
                {openItem.visit_outcome === 'return_visit' && (
                  <div data-testid="approval-return-visit" className="rounded-md border border-sky-500/40 bg-sky-500/5 p-3 text-sm space-y-1">
                    <p className="text-sky-200 text-xs uppercase tracking-wider font-bold">Return Visit Needed — Job stays open</p>
                    <p className="text-white">{openItem.return_visit?.reason_label}{openItem.return_visit?.note ? ` — ${openItem.return_visit.note}` : ''}</p>
                    {openItem.return_visit?.suggested && <p className="text-[#A3A3A3] text-xs">Suggested: {slotLabel(openItem.return_visit.suggested)}</p>}
                    {openItem.return_visit?.promised && <p className="text-amber-300 text-xs font-bold">CUSTOMER PROMISED — {slotLabel(openItem.return_visit.promised)}</p>}
                    <p className="text-[10px] text-[#737373]">Approving records this visit only — no invoice, payment or review request.</p>
                  </div>
                )}

                {openItem.materials_used && (
                  <div>
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Materials Used</p>
                    <p className="text-white whitespace-pre-wrap">{openItem.materials_used}</p>
                  </div>
                )}

                {openItem.recommended_followups && (
                  <div>
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Recommended Follow-ups</p>
                    <p className="text-white whitespace-pre-wrap">{openItem.recommended_followups}</p>
                  </div>
                )}

                {openItem.customer_signature_name && (
                  <div>
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-1">Customer Acknowledged</p>
                    <p className="text-white">{openItem.customer_signature_name}</p>
                  </div>
                )}

                {openItem.revision_note && (
                  <div className="bg-red-900/15 border border-red-700/40 rounded-md p-3">
                    <p className="text-red-400 text-xs uppercase tracking-wider mb-1 font-bold">Manager Revision Note</p>
                    <p className="text-white text-sm whitespace-pre-wrap">{openItem.revision_note}</p>
                  </div>
                )}

                {openItem.status === 'pending_approval' && (
                  <div className="space-y-3 pt-3 border-t border-[#262626]">
                    <Button
                      data-testid="approve-completion-btn"
                      onClick={() => approve(openItem)}
                      disabled={busy}
                      className="w-full bg-green-600 text-white hover:bg-green-700"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" /> {openItem.visit_outcome === 'return_visit' ? 'Approve Visit (Job stays open)' : 'Approve & Mark Completed'}
                    </Button>
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1" data-testid="revision-prompts">
                        {REVISION_PROMPTS.map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            data-testid={`revision-prompt-${p.key}`}
                            onClick={() => toggleRevisionItem(p.key)}
                            className={`px-2 py-1 text-[10px] rounded border font-medium ${
                              revisionItems.includes(p.key)
                                ? 'bg-red-900/30 border-red-700/40 text-red-300'
                                : 'bg-[#0A0A0A] border-[#262626] text-[#A3A3A3] hover:border-[#3f3f3f]'
                            }`}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                      <Textarea
                        data-testid="revision-note-input"
                        value={revisionNote}
                        onChange={(e) => setRevisionNote(e.target.value)}
                        placeholder="What needs to change? (required to reject)"
                        rows={3}
                        className="bg-[#0A0A0A] border-[#262626] text-white"
                      />
                      <Button
                        data-testid="reject-completion-btn"
                        onClick={() => reject(openItem)}
                        disabled={busy}
                        variant="outline"
                        className="w-full border-red-700/40 text-red-400 hover:bg-red-900/20"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" /> Request Revisions
                      </Button>
                    </div>
                  </div>
                )}

                {/* Invoice decision for approved-but-not-billed completions (Wave C #9) */}
                {openItem.status === 'approved' && !openItem.invoice_status && (
                  <div className="space-y-3 pt-3 border-t border-[#262626]" data-testid="invoice-decision-block">
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Set Invoice Status</p>
                    <div className="grid grid-cols-2 gap-2">
                      {INVOICE_OPTIONS.map((o) => (
                        <button
                          key={o.key}
                          type="button"
                          data-testid={`invoice-option-${o.key}`}
                          onClick={() => setInvoiceStatus(o.key)}
                          className={`px-3 py-2 text-xs rounded border font-medium ${
                            invoiceStatus === o.key
                              ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
                              : 'bg-[#0A0A0A] border-[#262626] text-white hover:border-[#3f3f3f]'
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    <Button data-testid="set-invoice-status-btn" onClick={() => submitInvoiceDecision(openItem)} disabled={busy} className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                      Set Invoice Status
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default JobApprovals;
